/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject } from 'react'
import { Field, Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { useGetSecurityQuery, useUpdateSecurityMutation } from 'api/account'
import { ChangeSecurityQuestion } from 'models/account'
import { useAppSelector } from 'store/hooks'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    form: RefObject<FormikProps<ChangeSecurityQuestion>>
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ChangeSecurityQuestionPasswordForm({ form }: Props) {
    const { appSession: { user } } = useAppSelector((state) => state.app)
    const { security_question: securityQuestion, security_answer: securityAnswer } = user!
    const [updateSecurity] = useUpdateSecurityMutation({ fixedCacheKey: 'update-security' })
    const { data: securityQuestions } = useGetSecurityQuery()

    function touchedValidation({ security_question_id, security_answer }: { security_question_id: string, security_answer: string }) {
        const iterate = (a: string, b: string) => a !== b
        return !(iterate(security_question_id, securityQuestion?.id) || iterate(security_answer, securityAnswer))
    }

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return (
        <Formik
            initialValues={{
                security_question_id: securityQuestion?.id || '',
                security_answer: securityAnswer,
                password: ''
            }}
            validationSchema={
                Yup.object({
                    security_question_id: Yup.string()
                        .required('Required'),
                    security_answer: Yup.string()
                        .required('Required'),
                    password: Yup.string()
                        .required('Required')

                })
            }
            innerRef={form}
            onSubmit={({ security_question_id, security_answer, password }) => {
                updateSecurity({ security_question_id, security_answer, password })
            }}
        >
            {({ isSubmitting, values }) => (
                <Form id={'changeSecurityQuestionForm'}>
                    <Field
                        id={'question'}
                        label={'Security Question'}
                        name={'security_question_id'}
                        type={'select'}
                        component={NXUIForm.SelectField}
                        list={securityQuestions?.map(({ id, question }) => ({
                            label: question,
                            value: id
                        }))}
                    />
                    <Field
                        id={'answer'}
                        label={'Answer'}
                        name={'security_answer'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        id={'password'}
                        label={'Password'}
                        name={'password'}
                        type={'password'}
                        autoComplete={'password'}
                        disabled={touchedValidation(values) || isSubmitting}
                        component={NXUIForm.InputField}
                    />
                </Form>
            )}
        </Formik>
    )
}
